import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormArray, Validators, FormGroup, AbstractControl } from '@angular/forms';
import { ApiService } from 'src/app/services/api/api.service';
import { EstateControlService } from 'src/app/services/estate-control.service';
import { TabControlService } from 'src/app/services/tab-control.service';
import { NotificationService } from 'src/app/services/notification.service';
import { createIDsValidatorEstate } from 'src/app/services/validators/custom.validators';
import { ValidationService } from 'src/app/services/validators/validation.service';
import { environment } from 'src/environments/environment';
import { findObjectIndex, formatDate, getEarliestDateFor18Years } from 'src/app/util/Helper';
import * as Constants from "src/app/constants/constants";
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ExpansionPanel } from 'src/app/components/section/section.component';
import { debounceTime, distinctUntilChanged, merge, of, Subscription } from 'rxjs';

@Component({
  selector: 'app-step3-beneficiary-estate',
  templateUrl: './step3-beneficiary-estate.component.html',
  styleUrls: ['./step3-beneficiary-estate.component.scss'],
  animations: [
    trigger('bounceIn', [
      state('in', style({ opacity: 1, transform: 'scale(1)' })),
      transition('void => *', [
        style({ opacity: 0, transform: 'scale(0.8)' }),
        animate('0.15s ease-in-out')
      ]),
      transition('* => void', [
        animate('0.15s ease-in-out', style({ opacity: 0, transform: 'scale(0.7)' }))
      ])
    ]),
    trigger('slideInRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('0.15s ease-in-out', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('0.15s ease-in-out', style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ]
})
export class Step3BeneficiaryEstateComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() scrollUp = new EventEmitter<number>();
  @ViewChild('suggestBox', { static: false }) suggestBox?: ElementRef;
  
  checkerPath: string = `${environment.hRef}assets/fi_check.png`;

  title: string = 'Beneficiaries';
  subtitle: string = 'Add one or more beneficiaries.';

  benTypeLabel: string = 'Select Beneficiary Type';
  benTypeHint: string = 'Beneficiary type';

  relationLabel: string = 'Relationship To Settlor';
  relationHint: string = 'Specify Relationship';

  titleLabel: string = 'Title';
  titleHint: string = 'Title';
  fNameLabel: string = 'First Name';
  fNameHint : string = 'First name';
  mNameLabel : string = 'Middle Name';
  mNameHint : string = 'Middle name';
  surnameLabel : string = 'Surname';
  surnameHint : string = 'Last name';
  genderLabel: string = 'Gender';
  genderHint: string = 'Select gender';
  phoneLabel : string = 'Primary Phone Number';
  emailLabel : string = 'Email Address';
  emailHint : string = 'Email address';
  dobLabel: string = 'Date of birth';
  dobHint: string = '01/01/1990';

  institutionNameLabel: string = 'Name of Institution';
  businessNatureLabel: string = 'Nature of Business';
  orgRegNumLabel: string = 'Organization Registration Number';
  orgAddressLabel: string = 'Organization Physical Address';

  institutionNameHint: string = 'Enter name';
  businessNatureHint: string = 'Enter nature of business';
  orgRegNumHint: string = 'Enter registration Number';
  orgAddressHint: string = 'Enter physical address';

  additionalDetailsText: string = 'Upload Documents';
  // Senior
  idLabel: string = 'ID/Passport Number';
  idHint: string = 'Enter ID or Passport Number';
  kraPinLabel: string = 'KRA PIN';
  kraHint: string = 'Enter KRA PIN';
  idFileLabel: string = 'Upload Copy of ID/Passport';
  // Minor
  birthCertLabel: string = 'Upload Birth Certificate of Child';

  regCertFileLabel: string = 'Upload Registration Certificate';
  kraFileLabel: string = 'Upload KRA PIN';

  addBenText: string = 'Add Another Beneficiary';
  removeBenText: string = 'Remove Beneficiary';

  addText: string = 'Beneficiary details saved';
  updateText: string = 'Beneficiary details updated'
  
  requiredFieldString: string = 'Required field';
  invalidDocString: string = 'Please attach a valid document';

  buttonDisabled: boolean = true;
  addDisabled: boolean = true;


  formErrors: { [key: string]: string } = {
    Category: '',
    Title: '',
    FirstName: '',
    MiddleName: '',
    LastName: '',
    Gender: '',
    Phone: '',
    Email: '',
    DOB: '',
    IDNo: '',
    KraPin: '',
    IDNoImage: '',
    BirthCertImage: '',
    Relationship: '',
    Other: '',

    InstitutionName: '',
    BusinessNature: '',
    OrgRegNum: '',
    OrgAddress: '',
    RegCertImage: '',
    KraPinImage: '',
  };

  validationMessages: { [key: string]: {} } = {
    Category: { required: this.requiredFieldString },
    Title: { required: this.requiredFieldString },
    FirstName: { required: this.requiredFieldString },
    MiddleName: { required: this.requiredFieldString },
    LastName: { required: this.requiredFieldString },
    Gender: { required: this.requiredFieldString },
    Phone: {
      required: this.requiredFieldString,
      pattern:'Invalid phone number',
      exist: `${
        this.beneficiaries[this.beneficiaries.length-1]
        && this.beneficiaries[this.beneficiaries.length-1].personalDone
        && this.beneficiaries[this.beneficiaries.length-1].personalDone == true ? 'This number is tied to an existing contact' : 'This number is tied to an existing beneficiary' }` },
    Email: {
      required: this.requiredFieldString,
      email: "Invalid email address",
      exist: `${
        this.beneficiaries[this.beneficiaries.length-1]
        && this.beneficiaries[this.beneficiaries.length-1].personalDone
        && this.beneficiaries[this.beneficiaries.length-1].personalDone == true ? 'This email is tied to an existing contact' : 'This email is tied to an existing beneficiary' }` },
    DOB:  { required: this.requiredFieldString, matDatepickerMax: 'Please select a valid date', matDatepickerMin: 'Please select a valid date' },
    IDNo:  { required: this.requiredFieldString, pattern:'Invalid ID or Passport number', exist: 'This ID is tied to an existing beneficiary' },
    KraPin: { required: this.requiredFieldString, pattern:'Invalid KRA PIN', exist: 'This PIN is tied to an existing beneficiary' },
    IDNoImage: { required: this.requiredFieldString, invalid: this.invalidDocString },
    BirthCertImage: { required: this.requiredFieldString, invalid: this.invalidDocString },
    Relationship: { required: this.requiredFieldString },
    Other: {required: this.requiredFieldString},

    InstitutionName: { required: this.requiredFieldString },
    BusinessNature: { required: this.requiredFieldString },
    OrgRegNum: { required: this.requiredFieldString },
    OrgAddress: { required: this.requiredFieldString },

    RegCertImage: { required: this.requiredFieldString, invalid: this.invalidDocString },
    KraPinImage: { required: this.requiredFieldString, invalid: this.invalidDocString },
  };

  benForm = this._formBuilder.group({
    beneficiaries: this._formBuilder.array([
      this.createBensFormGroup()
    ]),
  });

  guardianForm = this._formBuilder.group({
    guardians: this._formBuilder.array([
      this.createGuardiansFormGroup()
    ]),
  });

  // guardianForm = this._formBuilder.group({
  // }) as FormGroup & { [key: string]: AbstractControl };

  over18: any = '';
  endDate: any = this.maxDate;
  loadingText: string = 'Saving Beneficiary';

  loadingRemove: boolean = false;
  removeButtonText: string = 'Remove Beneficiary';
  removeContactText: string = 'Remove Contact';
  navigationRequested: boolean = false;
  currentIndex: number = 0;
  shouldInsert: boolean = false;
  
  selfRecords: any = []; // For suggesting self
  recordUpdate: any;

  benPanels: ExpansionPanel[][] = [];
  guardianPanel: ExpansionPanel[][] = [];
  private formChangesSubscription: Subscription[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private notificationService: NotificationService,
    private tabControlService: TabControlService,
    public estateControlService: EstateControlService,
    private validationService: ValidationService,
    private apiService: ApiService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    let records: any = this.settlors;
    if (this.planType == 1) records = this.testators;

    this.selfRecords.push(records[0]);
    if (records.length > 1) this.selfRecords.push(records[1]);

    if (this.beneficiaries && this.beneficiaries.length > 0) {
      if (this.beneficiaries[0].personalDone
        && this.beneficiaries[0].personalDone == true) {
        const beneficiariesArray = this.benForm.get('beneficiaries') as FormArray;

        // Clear existing beneficiaries
        while (beneficiariesArray.length !== 0) {
          beneficiariesArray.removeAt(0);
        }

        // Add beneficiaries from storage
        this.beneficiaries.forEach((beneficiary: any) => {
          beneficiariesArray.push(this.createBensFormGroup(beneficiary));
        });

        if (this.planType == 0) {
          this.estateControlService.updateStorageObject();
        } else {
          this.estateControlService.updateStorageObjectWill();
        }
      }
    }

    if (this.planType == 0) {
      if (this.estateControlService.activeTab == 3) {
        this.populateData();
      }
    } else {
      if (this.estateControlService.activeTab == 5) {
        this.populateData();
      }
    }

    // Initialize all bensPanels with their FormGroup already created
    this.getBens().controls.forEach((_, i) => {
      if (!this.benPanels[i]) {
        this.benPanels[i] = this.generatePanelsForBeneficiary(i);
      }
    });

    this.setupFormChangeListeners();

    this.benForm.valueChanges.subscribe(() => {
      this.logErrors();
      // console.log('::::::::::::::::::::::::::::: FORM + CONTROLS DEBUG\n', this.getBens().controls);
    });
    this.guardianForm.valueChanges.subscribe(() => {
      this.logErrors(1);
    });
  }

  ngAfterViewInit() {
    this.beneficiaries.forEach((_: any, i: number) => {
      this.benPanels[i] = this.generatePanelsForBeneficiary(i);
    });
  }

  ngOnDestroy(): void {
    this.cleanupFormChangeListeners();
  }

  logErrors(switcher: 0 | 1 = 0) {
    this.formErrors = this.validationService.logValidationErrors(
      switcher == 1 ? this.guardianForm : this.benForm,
      this.formErrors,
      this.validationMessages
    );
  }
  
  getDateDifference(date?: string) :boolean {
    if (date) {
      const currentDate = new Date();
      const dobDate = new Date(date);
      const ageDifferenceInMilliseconds = currentDate.getTime() - dobDate.getTime();
      const ageDate = new Date(ageDifferenceInMilliseconds);

      // Calculate age based on the year.
      const age = Math.abs(ageDate.getUTCFullYear() - 1970);
      if (age < 18) {
        return false;
      } else {
        return true;
      }
    } else return true;
    
  }

  businessNatureEmpty: boolean = true;

  get loading(): boolean {
    return this.notificationService.loading;
  }

  get region(): any {
    return this.estateControlService.regionType;
  }
  get planType(): 0 | 1 {
    return this.estateControlService.planType;
  }
  get beneficiaries(): any {
    return this.estateControlService.beneficiaries;
  }
  get settlors(): any {
    return this.estateControlService.settlors;
  }
  get testators(): any {
    return this.estateControlService.testators;
  }

  get minDate() {
    return this.tabControlService.minDOBDate;
  }
  get maxDate() {
    return this.tabControlService.maxDOBDate;
  }

  get loadingTitles(): boolean {
    return this.tabControlService.loadingTitles;
  }
  get TitleOptions(): any {
    return this.tabControlService.TitleOptions;
  }
  get BeneficiaryOptions():any {
    return this.tabControlService.BeneficiaryOptions;
  }
  get GenderOptions():any {
    return this.tabControlService.GenderOptions;
  }
  get RelationOptions():any {
    return this.tabControlService.RelationOptions;
  }
  Relationships: any = this.RelationOptions.filter((relation: string) => relation !== 'Self');

  get loadingBizNature(): boolean {
    return this.tabControlService.loadingBizNature;
  }
  get BusinessNatureOptions(): any {
    return this.tabControlService.BusinessNatureOptions;
  }

  get saveProgress(): ()=> void {
    return this.estateControlService.saveProgress;
  }

  async populateData(): Promise<void> {
    if (this.TitleOptions.length === 0) {
      this.tabControlService.loadingTitles = true;
      await this.tabControlService.getTitlesList();
      this.tabControlService.loadingTitles = false;
    }
    if (this.BusinessNatureOptions.length === 0) {
      this.tabControlService.loadingBizNature = true;
      await this.tabControlService.getBusinessNatOptionsList();
      this.tabControlService.loadingBizNature = false;
    }
  }

  organizeValues(items: any): string {
    try {
      let output: string = items[0];
      for (let i=1; i<items.length; i++) {
        output = output + ', ' +items[i];
      }
      // console.log('Final output : ', output);
      return output;
    } catch (error) {
      console.log('Error : ', error);
      return '';
    }
  }

  arrayContainsValue(arr: string | null | undefined, value: string) {
    return arr && arr.includes(value);
  }

  updateIDObject(index: number, object: any): void {
    if (!object) return;
    const currents = this.beneficiaries;
    const latestRecord = currents[index];
    let updatedRecord = {
      ...latestRecord,
      ...{IDNoImage: object}
    };

    Object.assign(currents[index], updatedRecord);
    if (this.planType == 0) {
      this.estateControlService.updateRecord(3, currents);
    } else {
      this.estateControlService.updateRecordWill(5, currents);
    }
  }
  updateKraObject(index: number, object: any): void {
    if (!object) return;
    const currents = this.beneficiaries;
    const latestRecord = currents[index];
    let updatedRecord = {
      ...latestRecord,
      ...{KraPinImage: object}
    };

    Object.assign(currents[index], updatedRecord);
    if (this.planType == 0) {
      this.estateControlService.updateRecord(3, currents);
    } else {
      this.estateControlService.updateRecordWill(5, currents);
    }
  }
  updateBirthCertObject(index: number, object: any): void {
    if (!object) return;
    const currents = this.beneficiaries;
    const latestRecord = currents[index];
    let updatedRecord = {
      ...latestRecord,
      ...{BirthCertImage: object}
    };

    Object.assign(currents[index], updatedRecord);
    if (this.planType == 0) {
      this.estateControlService.updateRecord(3, currents);
    } else {
      this.estateControlService.updateRecordWill(5, currents);
    }
  }
  updateRegCertObject(index: number, object: any): void {
    if (!object) return;
    const currents = this.beneficiaries;
    const latestRecord = currents[index];
    let updatedRecord = {
      ...latestRecord,
      ...{RegCertImage: object}
    };

    Object.assign(currents[index], updatedRecord);
    if (this.planType == 0) {
      this.estateControlService.updateRecord(3, currents);
    } else {
      this.estateControlService.updateRecordWill(5, currents);
    }
  }

  onBenTypeChange(index: number): void {
    this.ngZone.runOutsideAngular(() => {
      // Batch all form control updates
      setTimeout(() => {
        const formGroup = this.getBensFormGroup(index);
        const beneType = formGroup.get('Category')?.value;

        if (!beneType) return;

        // Disable automatic change detection during updates
        this.ngZone.run(() => {
          if (beneType.toLowerCase() === 'individual') {
            // Batch clear organization controls
            ['InstitutionName', 'BusinessNature', 'OrgRegNum', 'KraPin', 'OrgAddress']
              .forEach(controlName => {
                formGroup.get(controlName)?.clearValidators();
                formGroup.get(controlName)?.setValue('');
              });

            // Batch add individual validators
            ['Relationship', 'FirstName', 'LastName', 'DOB']
              .forEach(controlName => {
                this.validationService.addOrRemoveValidator(true, formGroup.get(controlName)!);
                if (controlName === 'Relationship') {
                  this.validationService.addOrRemoveValidationOnValue(
                    this.getBensFormGroup(index).get("Relationship")!,
                    "Other",
                    this.getBensFormGroup(index),
                    "Other"
                  );
                }
            });
            
          } else {
            // Batch clear individual controls
            ['Title', 'FirstName', 'MiddleName', 'LastName', 'DOB', 'Gender',
              'Relationship', 'Other', 'Phone', 'Email'
            ].forEach(controlName => {
              formGroup.get(controlName)?.clearValidators();
              formGroup.get(controlName)?.setValue('');
            });
            this.toggleFormControls(index, true);

            // Batch add institution validators
            ['InstitutionName', 'BusinessNature', 'OrgRegNum', 'OrgAddress', 'KraPin']
              .forEach(controlName => {
                this.validationService.addOrRemoveValidator(true, formGroup.get(controlName)!);
                if (controlName === 'KraPin') {
                  this.validationService.addOrRemove3Validators(
                    true,
                    this.getBensFormGroup(index).get('KraPin')!,
                    [Validators.required,
                      Validators.pattern(/^[AP]\d{9}[A-Z]$/),
                      createIDsValidatorEstate('PIN', this.getBens(), this.beneficiaries)
                    ]
                  );
                }
            });
          }

          // Force panel regeneration after category change
          this.benPanels[index] = this.generatePanelsForBeneficiary(index);
          this.cdr.markForCheck();
        });
      });
    });
  }

  hasSelfRelationship: boolean = false;
 
  trackByFn(index: number, item: any) {
    return index; // Or a unique ID if available
  }
  onRelationshipChange(index: number): void {
    const value = this.getBensFormGroup(index)?.get('Relationship')?.value;
    if (value) {
      switch (value.toLowerCase()) {
        case 'self':
        case 'consultant':
        case 'financial advisor':
        case 'lawyer':
        case 'parent':
        case 'spouse':
          if (this.endDate !== getEarliestDateFor18Years()) {
            this.getBensFormGroup(index)?.get('DOB')?.setValue('');
            this.endDate = getEarliestDateFor18Years();
          }
          if (value.toLowerCase() == 'self') {
            const fields = this.benForm.get('beneficiaries') as FormArray;
            this.recordUpdate = this.selfRecords[0];
            let counter: number = 0;
            
            this.hasSelfRelationship = fields.controls.some(group => {  
              if (group.get('Relationship')?.value.toLowerCase() === 'self') {
                counter++;
              }
              return counter > 1;
            });
            // To autofill second settlor / testator
            if (this.hasSelfRelationship && this.selfRecords.length > 1)
              this.recordUpdate = this.selfRecords[1];
      
            fields.removeAt(index);
            fields.insert(index, this.createBensFormGroup(this.recordUpdate, value, index));
      
            this.benForm.setControl('beneficiaries', fields);
            this.toggleFormControls(index, false);
          } else {
            this.toggleFormControls(index, true);
          }
          break;
        default:
          if (this.endDate !== this.maxDate)
            this.getBensFormGroup(index)?.get('DOB')?.setValue('');
          this.endDate = this.maxDate;
          this.toggleFormControls(index, true);
          break;
      }
    }
  }

  toggleFormControls(index: number, enabled: boolean): void {
    if (enabled) { // Reset form when relationship != self
      const isSelfRecord: boolean = this.selfRecords.some((record: any) => {
        return this.getBensFormGroup(index).get('IDNo')?.value == record.IDNo;
      });

      if (isSelfRecord) {
        this.getBensFormGroup(index)?.get('Title')?.enable();
        this.getBensFormGroup(index)?.get('FirstName')?.enable();
        this.getBensFormGroup(index)?.get('MiddleName')?.enable();
        this.getBensFormGroup(index)?.get('LastName')?.enable();
        this.getBensFormGroup(index)?.get('Phone')?.enable();
        this.getBensFormGroup(index)?.get('Email')?.enable();
        this.getBensFormGroup(index)?.get('IDNoImage')?.enable();
        this.getBensFormGroup(index)?.get('KraPinImage')?.enable();
        
        this.getBensFormGroup(index)?.get('Title')?.setValue('');
        this.getBensFormGroup(index)?.get('FirstName')?.setValue('');
        this.getBensFormGroup(index)?.get('MiddleName')?.setValue('');
        this.getBensFormGroup(index)?.get('LastName')?.setValue('');

        this.getBensFormGroup(index).get('Phone')?.setValue('7');    
        this.getBensFormGroup(index)?.get('Email')?.setValue('');
        this.getBensFormGroup(index)?.get('Gender')?.setValue('');
        this.getBensFormGroup(index)?.get('DOB')?.setValue('');

        this.getBensFormGroup(index)?.get('KraPin')?.setValue('');

        this.getBensFormGroup(index)?.get('IDNoImage')?.setValue('');
        this.getBensFormGroup(index)?.get('KraPinImage')?.setValue('');
      }

    } else {
      // For 'self' option under relationship
      this.getBensFormGroup(index)?.get('Title')?.disable();
      this.getBensFormGroup(index)?.get('FirstName')?.disable();
      this.getBensFormGroup(index)?.get('MiddleName')?.disable();
      this.getBensFormGroup(index)?.get('LastName')?.disable();
      this.getBensFormGroup(index)?.get('Phone')?.disable();
      this.getBensFormGroup(index)?.get('Email')?.disable();
      this.getBensFormGroup(index)?.get('IDNoImage')?.disable();
      this.getBensFormGroup(index)?.get('KraPinImage')?.disable();
    }
    this.benForm.updateValueAndValidity();
  }

  createBensFormGroup(beneficiary: any = null, value: any = null, index?: number): FormGroup {
    let form: FormGroup;

    const setIndex = index ?? this.beneficiaries.length-1;

    if (beneficiary) {
      const contact = beneficiary.ContactPersons && beneficiary.ContactPersons[0] ? beneficiary.ContactPersons[0] : null;

      form = this._formBuilder.group({
        Category: [`${value ? this.BeneficiaryOptions[0] : beneficiary.Category || this.BeneficiaryOptions[0]}`, Validators.required],
        Title: [`${beneficiary.Title || ''}`],
        FirstName: [`${beneficiary.FirstName || ''}`],
        MiddleName: [`${beneficiary.MiddleName || ''}`],
        LastName: [`${beneficiary.LastName || ''}`],
        Gender: [`${beneficiary.Gender || ''}`],
        Phone: [`${beneficiary.Phone || ''}`],
        Email: [`${beneficiary.Email || ''}`, Validators.email],
        DOB: [beneficiary.DOB ? new Date(beneficiary.DOB) : null],
        Relationship: [`${value ? value : beneficiary.Relationship || ''}`],
        Other: [`${beneficiary.Other || ''}`],
        ContactPersons:  this._formBuilder.array([
          this.createContactsGroup(contact, null, beneficiary)
        ]),
  
        // IDNo: [''],
        KraPin: [`${beneficiary.KraPin || ''}`],
        IDNoImage: [''],
        BirthCertImage: [''],
  
        InstitutionName: [`${beneficiary.InstitutionName || ''}`],
        BusinessNature: [`${beneficiary.BusinessNature || ''}`],
        OrgRegNum: [`${beneficiary.OrgRegNum || ''}`],
        OrgAddress: [`${beneficiary.OrgAddress || ''}`],
  
        RegCertImage: [''],
        KraPinImage: ['']
      }) as FormGroup & { [key: string]: AbstractControl };

      if (beneficiary.ContactPersons && beneficiary.ContactPersons.length > 1) {
        const contactArray = form.get('ContactPersons') as FormArray;

        beneficiary.ContactPersons.forEach((contact: any, index: number) => {
          if (index > 0) contactArray.push(this.createContactsGroup(contact, contactArray, beneficiary));
        });
      }

      if (beneficiary.IDNoImage) {
        if (beneficiary.IDNoImage.stringValue && beneficiary.IDNoImage.stringValue.length > 0) {
          form.get('IDNoImage')?.setValue(beneficiary.IDNoImage.stringValue);
        }
      }
      if (beneficiary.KraPinImage) {
        if (beneficiary.KraPinImage.stringValue && beneficiary.KraPinImage.stringValue.length > 0) {
          form.get('KraPinImage')?.setValue(beneficiary.KraPinImage.stringValue);
        }
      }
      if (beneficiary.BirthCertImage) {
        if (beneficiary.BirthCertImage.stringValue && beneficiary.BirthCertImage.stringValue.length > 0) {
          form.get('BirthCertImage')?.setValue(beneficiary.BirthCertImage.stringValue);
        }
      }
      if (beneficiary.RegCertImage) {
        if (beneficiary.RegCertImage.stringValue && beneficiary.RegCertImage.stringValue.length > 0) {
          form.get('RegCertImage')?.setValue(beneficiary.RegCertImage.stringValue);
        }
      }

      if (!beneficiary.personalDone || beneficiary.Category === 'Individual' || value) {
        form.get('FirstName')?.addValidators(Validators.required);
        form.get('LastName')?.addValidators(Validators.required);
        form.get('Gender')?.addValidators(Validators.required);
        form.get('DOB')?.addValidators(Validators.required);
        form.get('Relationship')?.addValidators(Validators.required);

        this.validationService.addOrRemoveValidationOnValue(
          form.get("Relationship")!,
          "Other",
          form,
          "Other"
        );

        const isAdult = this.getDateDifference(beneficiary.DOB);
        if (beneficiary.contactsDone || value) {
          if (isAdult) {
            if (beneficiary.contactsDone ) {
              form.get('IDNoImage')?.addValidators(Validators.required);
              if (this.region === 0) 
                form.get('KraPinImage')?.addValidators(Validators.required);
            }

            if (!beneficiary.done || value) {
              // Autofill for self relationship
              this.selfRecords.some((record: any) => {
                if (
                  value ||
                  beneficiary.Relationship == 'Self' &&
                  beneficiary.FirstName == record.FirstName &&
                  beneficiary.LastName == record.LastName &&
                  beneficiary.Phone == record.Phone &&
                  beneficiary.Email == record.Email
                ) {
                  if (record.IDNoImage.stringValue && record.IDNoImage.stringValue.length > 0) {
                    this.updateIDObject(setIndex, record.IDNoImage);
                    form.get('IDNoImage')?.setValue(record.IDNoImage.stringValue);
                  }

                  if (record.KraPinImage.stringValue && record.KraPinImage.stringValue.length > 0) {
                    this.updateKraObject(setIndex, record.KraPinImage);
                    form.get('KraPinImage')?.setValue(record.KraPinImage.stringValue);
                  }
                }
              })
            }
          } else {
            form.get('BirthCertImage')?.addValidators(Validators.required);
          }
          // disable DOB input from getting updated on the current formgroup
          form.get('DOB')?.disable();
        }

        if (value || beneficiary.Relationship == 'Self') {
          form.get('Title')?.disable();
          form.get('FirstName')?.disable();
          form.get('MiddleName')?.disable();
          form.get('LastName')?.disable();
          form.get('Phone')?.disable();
          form.get('Email')?.disable();
          form.get('IDNoImage')?.disable();
          form.get('KraPinImage')?.disable();

          if (this.planType == 0 && this.estateControlService.activeTab == 3 || this.planType == 1 && this.estateControlService.activeTab == 5) {
            this.notificationService.viewToast(
              'info',
              `Beneficiary ${beneficiary.FirstName+' '+beneficiary.LastName} is also a ${this.planType == 1 ? 'testator' : 'settlor'}.`,
              `To modify saved details and documents, edit the ${this.planType == 1 ? 'testator' : 'settlor'} record itself`
            );
          }
        }
      } else {
        form.get('InstitutionName')?.addValidators(Validators.required);
        form.get('BusinessNature')?.addValidators(Validators.required);
        form.get('OrgRegNum')?.addValidators(Validators.required);
        form.get('OrgAddress')?.addValidators(Validators.required);
        form.get('KraPin')?.addValidators(Validators.required);

        if (beneficiary.contactsDone) {
          form.get('RegCertImage')?.addValidators(Validators.required);
          form.get('KraPinImage')?.addValidators(Validators.required);
        }
      }

    } else {

      if (
        this.beneficiaries && this.beneficiaries.length > 0 &&
        !(this.beneficiaries[0].personalDone && this.beneficiaries[0].personalDone == true)
      ) {
        if (this.planType == 0) {
          this.estateControlService.refreshRecord(3);
        } else {
          this.estateControlService.refreshRecordWill(5);
        }
      }
      
      let currentBens = this.beneficiaries;
      let currentRecord = {personalDone: false, contactsDone: false, docsDone: false, done: false};

      if (
        (this.benForm && this.getBens().length > 0) || this.beneficiaries &&
        (this.beneficiaries.length > 0 && !(this.beneficiaries[0].personalDone) ) ||
        this.beneficiaries.length == 0
      ) {
        currentBens.push(currentRecord);

        if (this.planType == 0) {
          this.estateControlService.updateRecord(3, currentBens);
        } else {
          this.estateControlService.updateRecordWill(5, currentBens);
        }
      }
      
      form = this._formBuilder.group({
        Category: [this.BeneficiaryOptions[0], Validators.required],
        Title: [''],
        FirstName: ['', Validators.required],
        MiddleName: [''],
        LastName: ['', Validators.required],
        Gender: ['', Validators.required],
        Phone: [''],
        Email: ['', Validators.email],
        DOB: [, Validators.required],
        Relationship: ['', Validators.required],
        Other: [''],
        ContactPersons: this._formBuilder.array([
          this.createContactsGroup(null, null, this.beneficiaries[this.beneficiaries.length-1])]
        ),
  
        // IDNo: [''],
        KraPin: [''],
        IDNoImage: [''],
        BirthCertImage: [''],
  
        InstitutionName: [''],
        BusinessNature: [''],
        OrgRegNum: [''],
        OrgAddress: [''],
  
        RegCertImage: [''],
        KraPinImage: ['']
      }) as FormGroup & { [key: string]: AbstractControl };

      this.validationService.addOrRemoveValidationOnValue(
        form.get("Relationship")!,
        "Other",
        form,
        "Other"
      );
    }

    if (this.benForm && this.getBens().length > 0) {
      this.validationService.addOrRemoveOneValidator(
        true,
        form.get('Phone')!,
        createIDsValidatorEstate('TEL', this.getBens(), this.beneficiaries)
      );

      this.validationService.addOrRemoveSpecificValidators(
        true,
        form.get('Email')!,
        [ Validators.email,
          createIDsValidatorEstate('EMAIL', this.getBens(), this.beneficiaries)
        ]
      );

      // For 'self' option control
      const fields = this.benForm.get('beneficiaries') as FormArray;
      let counter: number = 0;
      
      this.hasSelfRelationship = fields.controls.some(group => {  
        if (group.get('Relationship')?.value.toLowerCase() === 'self') {
          counter++;
        }
        return counter == this.selfRecords.length;
      });
    }

    return form;
  };

  createContactsGroup(contact: any = null, formArray: FormArray | null, beneficiary: any): FormGroup {
    let contactForm: FormGroup;
    if (contact) {
      contactForm = this._formBuilder.group({
        Title: [`${contact.Title || ''}`],
        FirstName: [`${contact.FirstName || ''}`],
        MiddleName: [`${contact.MiddleName || ''}`],
        LastName: [`${contact.LastName || ''}`],
        Phone: [`${contact.Phone || ''}`],
        Email: [`${contact.Email || ''}`]
      });

    } else {
      contactForm = this._formBuilder.group({
        Title: [''],
        FirstName: [''],
        MiddleName: [''],
        LastName: [''],
        Phone: [''],
        Email: ['']
      });
    }

    if (beneficiary?.personalDone && beneficiary?.Category === this.BeneficiaryOptions[1] ) {
      contactForm.get('FirstName')?.addValidators(Validators.required);
      contactForm.get('LastName')?.addValidators(Validators.required);

      if (this.benForm) {
        if (formArray) {
          this.validationService.addOrRemoveSpecificValidators(
            true,
            contactForm.get('Phone')!,
            [Validators.required, createIDsValidatorEstate('TEL', formArray, this.beneficiaries[this.beneficiaries.length-1].ContactPersons)]
          );
          this.validationService.addOrRemove3Validators(
            true,
            contactForm.get('Email')!,
            [Validators.required, Validators.email, createIDsValidatorEstate('EMAIL', formArray, this.beneficiaries[this.beneficiaries.length-1].ContactPersons)]
          );
        } else {
          contactForm.get('Phone')?.addValidators(Validators.required);
          contactForm.get('Email')?.addValidators([Validators.required, Validators.email]);
        }
      }
    }
    
    return contactForm;
  };

  async addNewBen() {
    const fields = this.benForm.get('beneficiaries') as FormArray;
    const newFormGroup = this.createBensFormGroup();
    fields.push(newFormGroup);
    this.benForm.setControl('beneficiaries', fields);
    this.guardians = [{done: false}];

    // Wait for view to update
    await new Promise(resolve => setTimeout(resolve));
    const newIndex = fields.length - 1;
    this.benPanels[newIndex] = this.generatePanelsForBeneficiary(newIndex);
    
    // Set up listeners after panels are generated
    this.setupFormChangeListenersForIndex(newIndex);
    this.cdr.detectChanges();
  }
  async addNewContact(index:number) {
    const fields = this.getContacts(index);
    fields.push(this.createContactsGroup(null, fields, this.beneficiaries[index]));
    this.getBensFormGroup(index).setControl('ContactPersons', fields);

    let currentBens = this.beneficiaries;
    const latestRecord = currentBens[index];
    const record = this.getContacts(index);

    const existingContacts = latestRecord.ContactPersons || [];
    const newContacts = record.getRawValue() || [];

    const updatedRecord = {
      ...latestRecord,
      ContactPersons: [
        ...existingContacts,
        ...newContacts.filter(
          (newContact: any) =>
            !existingContacts.some(
              (existingContact: any) => existingContact.Phone === newContact.Phone
            )
        )
      ]
    };

    Object.assign(currentBens[index], updatedRecord);

    if (!environment.production) console.log('current Bens: ', this.beneficiaries);

    if (this.planType == 0) {
      this.estateControlService.updateRecord(3, currentBens);
    } else {
      this.estateControlService.updateRecordWill(5, currentBens);
    }
  }
  
  async removeBen(i:number) {
    this.currentIndex = i;
    const fields = this.benForm.get('beneficiaries') as FormArray;
    let currentBens = this.beneficiaries;

    if (currentBens[i] && currentBens[i].BeneficiaryID) {
      this.removeButtonText = 'Removing Beneficiary';
      this.loadingRemove = true;

      try {
        if (this.estateControlService.TrustID !== null) {
          const result = await this.estateControlService.removeRecord(
            'beneficiary',
            currentBens[i].BeneficiaryID,
            parseInt(this.estateControlService.TrustID),
            environment.baseUrl + Constants.estateBeneficiaryURL
          );
  
          if (result === 1) {
            this.notificationService.viewToast('success', 'Beneficiary removed successfully');
            fields.removeAt(i);
            currentBens.splice(i, 1);

            if (this.planType == 0) {
              this.estateControlService.updateRecord(3, currentBens);
            } else {
              this.estateControlService.updateRecordWill(5, currentBens);
            }
          } else {
            this.notificationService.viewToast('error', 'Error removing record');
          }
        } else {
          console.log('Relevant IDs not found');
        }
      } catch (error) {
        console.error(':: Error !! ', error);
      }
      this.removeButtonText = this.removeBenText;
      this.loadingRemove = false;
    } else {
      if (currentBens[i]) {
        fields.removeAt(i);
        // Remove beneficiary at index
        currentBens.splice(i, 1);

        if (this.planType == 0) {
          this.estateControlService.updateRecord(3, currentBens);
        } else {
          this.estateControlService.updateRecordWill(5, currentBens);
        }
      } else {
        fields.removeAt(i);
      }
    }

    if (this.getBens().length < 1) this.addNewBen();

    if (this.beneficiaries[this.beneficiaries.length-1] && this.beneficiaries[this.beneficiaries.length-1].Guardians) {
      this.guardians = this.beneficiaries[this.beneficiaries.length-1].Guardians;
    }

    setTimeout(()=> {
      this.generatePanelsForBeneficiary(i);
      this.beneficiaries.forEach((ben: any, index: number) => {
        this.updateDoneStatus(index, ben);
      });
    })
  }

  async removeContact(i:number, j: number) {
    const fields = this.getContacts(i);
    fields.removeAt(j);

    // Remove the contact from the beneficiaries' ContactPersons array
    const currentBens = [...this.beneficiaries]; // copy the array
    const ben = { ...currentBens[i] }; // copy the beneficiary object

    // Remove the contact at index j from ContactPersons
    const updatedContacts = ben.ContactPersons
      ? [
          ...ben.ContactPersons.slice(0, j),
          ...ben.ContactPersons.slice(j + 1)
        ]
      : [];

    // Update the beneficiary with the new contacts array
    ben.ContactPersons = updatedContacts;
    currentBens[i] = ben;

    // Sync with service
    if (this.planType == 0) {
      await this.estateControlService.updateRecord(3, currentBens);
    } else {
      await this.estateControlService.updateRecordWill(5, currentBens);
    }
    
    if (!environment.production) console.log('Updated beneficiaries:', this.beneficiaries);
  }

  getBens() : FormArray {  
    return this.benForm.get("beneficiaries") as FormArray;
  }
  getContacts(index: number) : FormArray {
    return this.getBens().at(index).get('ContactPersons') as FormArray;  
  }
  getBensFormGroup(index: number): FormGroup {
    return this.getBens().at(index) as FormGroup;
  }
  getContactsFormGroup(index: number, innerdex: number): FormGroup {
    return this.getContacts(index).at(innerdex) as FormGroup;
  }


  // ==========   Guardian controls    =========
  get GuardianOptions() {
    return this.tabControlService.GuardianOptions;
  };
  guardianTypeLabel: string = 'Guardian Type';
  guardianTypeHint: string = 'Select guardian type';
  guardians: any = [];
  showSuggestHint: boolean = false;
  showAssignPopup: boolean = false;

  briefTimeout: any;
  idObjectGuardian: any = null;
  kraObjectGuardian: any = null;
  suggestions: any = null;
  autofilled: boolean = false;
  
  updateGuardianIDObject(object: any): void {
    this.idObjectGuardian = object;
  }
  updateGuardianKraObject(object: any): void {
    this.kraObjectGuardian = object;
  }
  
  showSuggestBrief(): void {
    setTimeout(() => {
      this.showSuggestHint = true;
    }, 1000);

    this.briefTimeout = setTimeout(() => {
      this.showSuggestHint = false;
    }, 30000);
  }

  addGuardiansArray = (guardian: any = null) => {
    this.guardianForm.reset();
    const existing = this.guardianForm.get('guardians') as FormArray | null;

    if (existing) {
      // Clear existing FormArray
      while (existing.length > 0) {
        existing.removeAt(0);
      }
      this.guardians = [];
      // Push new guardian form group
      existing.push(this.createGuardiansFormGroup(guardian));

    } else {
      // If it doesn't exist yet, create it
      this.guardians = [];
      this.guardianForm.addControl(
        'guardians',
        this._formBuilder.array([
          this.createGuardiansFormGroup(guardian)
        ])
      );
    }

    this.guardianForm.updateValueAndValidity();
  };

  async suggestSettlors(type: 'ID' | 'NAME' , val: string): Promise<any> {
    if (this.autofilled) {
      this.resetForm();
    }
    
    this.suggestions = null;
    let validEntries: any = [];

    if (val.length > 2) {
        try {
            const fields = this.guardianForm.get('guardians') as FormArray;

            for (const element of this.settlors) {
                let exist = false;
                let trueStuff = element.FirstName.includes(val);

                if (type === 'ID') {
                  trueStuff = element.IDNo.includes(val);
                }

                if (trueStuff) {
                    for (let j = 0; j < fields.length - 1; j++) {
                        if (
                            fields.at(j).get('IDNo')?.value === element.IDNo ||
                            fields.at(j).get('Phone')?.value === element.Phone ||
                            fields.at(j).get('KraPin')?.value === element.KraPin) {
                            exist = true;
                        } else {
                          this.showSuggestHint = false;
                          clearTimeout(this.briefTimeout);
                        }
                    }

                    if (!exist && validEntries.length < 3 && !validEntries.some((entry: { IDNo: string; }) => entry.IDNo === element.IDNo)) {
                        validEntries.push(element);
                    }
                }
            }

            if (validEntries.length > 0) {
              this.suggestions = validEntries;
            }

            if (this.suggestions !== null) {
              const childDivHeight = this.suggestBox?.nativeElement.clientHeight;
              this.scrollUp.emit(childDivHeight);
            }
        } catch (error) {
            console.log('ERR !!! ', error);
        }
    }

    return this.suggestions;
  }

  resetForm(): void {
    try {
      this.autofilled = false;

      const guradian = this.getGuardians().at(this.getGuardians().length-1) as FormGroup;
      guradian.get('Title')?.setValue('');
      guradian.get('FirstName')?.setValue('');
      guradian.get('MiddleName')?.setValue('');
      guradian.get('LastName')?.setValue('');
      guradian.get('Phone')?.setValue('7');
      guradian.get('Email')?.setValue('');
      guradian.get('IDNo')?.setValue('');
      guradian.get('KraPin')?.setValue('');
      guradian.get('IDNoImage')?.setValue('');
      guradian.get('KraPinImage')?.setValue('');

      this.updateGuardianIDObject(null);
      this.updateGuardianKraObject(null);

    } catch (error) {
      console.log('Error!! ', error);
    }
  }

  patchSuggestedValues(index: number, value: any): void {
    try {
      if (value) {
        const fields = this.guardianForm.get('guardians') as FormArray;
        fields.removeAt(index);
        fields.push(this.createGuardiansFormGroup(value, true));

        this.guardianForm.setControl('guardians', fields);
        
        this.suggestions = null;
        this.autofilled = true;
      }

    } catch (error) {
      console.error('ERR ??? ', error);
    }
  }

  createGuardiansFormGroup(patchData?: any, patch?: boolean, benIndex?: number): FormGroup {
    let form: FormGroup;
    
    let category = this.tabControlService.GuardianOptions[0];

    if (this.guardianForm && this.getGuardians().length > 0) {
      category = this.tabControlService.GuardianOptions[1];
    }

    if (patchData) {
      if (patch) {
        this.autofilled = true;

        form = this._formBuilder.group({
          Category: [category , Validators.required],
          Title: [patchData.Title || ''],
          FirstName: [patchData.FirstName , Validators.required],
          MiddleName: [patchData.MiddleName || ''],
          LastName: [patchData.LastName, Validators.required],
          Phone: [patchData.Phone, Validators.required],
          Email: [patchData.Email, [Validators.required, Validators.email]],
          IDNo: [patchData.IDNo, [Validators.required, Validators.min(4)]],
          KraPin: [patchData.KraPin, [Validators.required, Validators.pattern(/^[AP]\d{9}[A-Z]$/) ]],
          IDNoImage: ['', Validators.required],
          KraPinImage: ['', Validators.required],
        });

      } else {
        form = this._formBuilder.group({
          Category: [patchData.Category || category , Validators.required],
          Title: [patchData.Title || ''],
          FirstName: [patchData.FirstName , Validators.required],
          MiddleName: [patchData.MiddleName || ''],
          LastName: [patchData.LastName || '', Validators.required],
          Phone: [patchData.Phone || '', Validators.required],
          Email: [patchData.Email || '', [Validators.required, Validators.email]],
          IDNo: [patchData.IDNo || '', [Validators.required, Validators.min(4)]],
          KraPin: [patchData.KraPin || '', [Validators.required, Validators.pattern(/^[AP]\d{9}[A-Z]$/) ]],
          IDNoImage: ['', Validators.required],
          KraPinImage: ['', Validators.required],
        });
      }

      // Diaspora region does not require KRA PIN
      if (this.region === 1) {
        this.validationService.addOrRemoveOneValidator(
          true,
          form.get('KraPin')!,
          Validators.pattern(/^[AP]\d{9}[A-Z]$/)
        );
        form.get('KraPinImage')?.clearValidators();
      }

      if (patchData.IDNoImage) {
        form.get('IDNoImage')?.setValue(patchData.IDNoImage.stringValue);
        this.updateGuardianIDObject(patchData.IDNoImage);
      }
      if (patchData.KraPinImage) {
        form.get('KraPinImage')?.setValue(patchData.KraPinImage.stringValue);
        this.updateGuardianKraObject(patchData.KraPinImage);
        // console.log('New KRA PIN :\n', this.kraObject);
      }

    } else {
      this.showSuggestBrief();

      this.autofilled = false;
      let currentRecord: any = {done: false};

      if (this.guardianForm) {
        this.guardians.push(currentRecord);
      }
      
      form = this._formBuilder.group({
        Category: [category , Validators.required],
        Title: [''],
        FirstName: ['', Validators.required],
        MiddleName: [''],
        LastName: ['', Validators.required],
        Phone: ['', Validators.required],
        Email: ['', [Validators.required, Validators.email]],
        IDNo: ['', [Validators.required, Validators.min(4)]],
        KraPin: ['', [Validators.required, Validators.pattern(/^[AP]\d{9}[A-Z]$/) ]],
        IDNoImage: ['', Validators.required],
        KraPinImage: ['', Validators.required],
      });
    }

    // Diaspora region does not require KRA PIN
      if (this.region === 1) {
        this.validationService.addOrRemoveOneValidator(
          true,
          form.get('KraPin')!,
          Validators.pattern(/^[AP]\d{9}[A-Z]$/)
        );
        form.get('KraPinImage')?.clearValidators();
      }
      
    if (this.guardianForm && this.getGuardians() && this.getGuardians().length > 0) {
      this.validationService.addOrRemove3Validators(
        true,
        form.get('Email')!,
        [Validators.required,
          Validators.email,
          createIDsValidatorEstate('EMAIL', this.getGuardians(), this.guardians)
        ]
      );
      this.validationService.addOrRemoveSpecificValidators(
        true,
        form.get('Phone')!,
        [Validators.required,
          createIDsValidatorEstate('TEL', this.getGuardians(), this.guardians)
        ]
      );
      this.validationService.addOrRemove3Validators(
        true,
        form.get('IDNo')!,
        [Validators.required,
          Validators.min(4),
          createIDsValidatorEstate('ID', this.getGuardians(), this.guardians)
        ]
      );
      this.validationService.addOrRemove3Validators(
        true,
        form.get('KraPin')!,
        [Validators.required,
          Validators.pattern(/^[AP]\d{9}[A-Z]$/),
          createIDsValidatorEstate('PIN', this.getGuardians(), this.guardians)
        ]
      );

      // Diaspora region does not require KRA PIN
      if (this.region === 1) {
        this.validationService.addOrRemoveSpecificValidators(
          true,
          form.get('KraPin')!,
          [
            Validators.pattern(/^[AP]\d{9}[A-Z]$/),
            createIDsValidatorEstate('PIN', this.getGuardians(), this.guardians)
          ]
        );
      }
    }

    return form;
  };

  async addNewGuardian() {
    this.updateGuardianObject();
    const fields = this.guardianForm.get('guardians') as FormArray;
    fields.push(this.createGuardiansFormGroup());
    this.guardianForm.setControl('guardians', fields); 
  }

  async removeGuardian(i:number) {
    this.currentIndex = i;
    const fields = this.guardianForm.get('guardians') as FormArray;
    fields.removeAt(i);
    // Remove guardian at index
    this.guardians.splice(i, 1);
    console.log('splice guardians : ', this.guardians);

    this.updateGuardianIDObject(this.guardians[this.guardians.length-1].IDNoImage);
    this.updateGuardianKraObject(this.guardians[this.guardians.length-1].KraPinImage);
  }
  
  getGuardians() : FormArray {  
    return this.guardianForm.get("guardians") as FormArray  
  }
  getGuardiansFormGroup(index: number): FormGroup {
    const guardians = this.guardianForm.get('guardians') as FormArray;
    return guardians.at(index) as FormGroup;
  }

  updateGuardianObject(): void {
    const guardianResult = this.guardianForm.get('guardians') as FormArray;
    const record: FormGroup = this.getGuardiansFormGroup(guardianResult.value.length - 1) as FormGroup;
    let item = record.value;
    let guard = this.guardians[this.guardians.length-1];

    item.IDNoImage = this.idObjectGuardian;
    item.KraPinImage = this.kraObjectGuardian;
    guard.done = true;

    const update = {...guard, ...item};
    Object.assign(this.guardians[this.guardians.length-1], update);

    this.updateGuardianIDObject(null);
    this.updateGuardianKraObject(null);
  }

  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // ======================    SAVE FUNCTIONS    ==========================

  async saveBeneficiary(index: number, record: FormGroup): Promise<void> {
    this.notificationService.isLoading(true);
    let tsMessage = this.addText;
    
    try {
      let temp = record.getRawValue();
      let originalContacts = [];
      let currentBens = this.beneficiaries;

      if (record.getRawValue().Category.toLowerCase() === 'individual') {
        temp.DOB = formatDate(record.getRawValue().DOB);
        temp.ContactPersons = [];

      } else {
        // Ensure ContactPersons is always an array
        if (temp.ContactPersons) {
          if (!Array.isArray(temp.ContactPersons)) temp.ContactPersons = [temp.ContactPersons];
          
          if (this.beneficiaries[index].ContactPersons?.length > 0) {
            temp.ContactPersons = temp.ContactPersons.map((contact: any) => {
              const matchingContact = this.beneficiaries[index].ContactPersons.find(
                (ogContact: any) => (ogContact && ogContact.ContactID && contact.Phone === ogContact.Phone)
              );
              return matchingContact 
                ? {...contact, ContactID: Number(matchingContact.ContactID)}
                : contact;
            });
          }

          originalContacts = temp.ContactPersons;
          
        } else {
          temp.ContactPersons = [];
        }
      }

      let data = {
        ...temp,
        ...{
          TrustID: this.estateControlService.TrustID,
          AccountType: this.estateControlService.planType == 0 ? 'PrivateTrust' : 'WillCreation'
        }
      };

      if (data.Other && data.Other != '') {
        const other = data.Other;
        data.Relationship = other;
      }
      if (data.hasOwnProperty('Other')) {
        delete data['Other'];
      }

      if (data.IDNoImage === '' || data.IDNoImage === null) {
        data.IDNoImage = {name: '', stringValue: ''};
      } else {
        data.IDNoImage = this.beneficiaries[index].IDNoImage;
      }
      if (data.KraPinImage === '' || data.KraPinImage === null) {
        data.KraPinImage = {name: '', stringValue: ''};
      } else {
        data.KraPinImage = this.beneficiaries[index].KraPinImage;
      }

      if (data.RegCertImage === '' || data.RegCertImage === null) {
        data.RegCertImage = {name: '', stringValue: ''};
      } else {
        data.RegCertImage = this.beneficiaries[index].RegCertImage;
      }
      if (data.BirthCertImage === '' || data.BirthCertImage === null) {
        data.BirthCertImage = {name: '', stringValue: ''};
      } else {
        data.BirthCertImage = this.beneficiaries[index].BirthCertImage;
      }

      
      if (currentBens[index].BeneficiaryID) {
        data = {...data, ...{BeneficiaryID: currentBens[index].BeneficiaryID}};
      }

      const response = await this.apiService.postRequest(
        environment.baseUrl + Constants.estateBeneficiaryURL, data);

      if (response.Status === 1) {
        const latestRecord = currentBens[index];
        // remove images from record to NOT replace object images!!!
        const recordValueWithoutImages = Object.fromEntries(
          Object.entries(record.getRawValue()).filter(([key]) => !key.includes('Image'))
        );

        const updatedRecord = {
          ...latestRecord,
          ...recordValueWithoutImages,
          ...{BeneficiaryID: response.BeneficiaryID}
        };
        Object.assign(currentBens[index], updatedRecord);

        if (this.planType == 0) {
          this.estateControlService.updateRecord(3, currentBens);
        } else {
          this.estateControlService.updateRecordWill(5, currentBens);
        }

        currentBens = this.beneficiaries; // update var
        const relation = data.Relationship.toLowerCase();
        let currentRecord = currentBens[index];

        if (currentRecord.contactsDone || relation == 'self') {
          tsMessage = this.updateText;
          currentRecord.docsDone = true;

          if (relation == 'self') {
            currentRecord.personalDone = true;
            currentRecord.contactsDone = true;
          }
          
          if (record.getRawValue().Category.toLowerCase() === 'individual') {
            currentRecord.ContactPersons = originalContacts;

            if (
              this.getBensFormGroup(this.getBens().length-1).get('DOB')?.value !== ''
              && this.getDateDifference(this.getBensFormGroup(this.getBens().length-1).get('DOB')?.value) === false
            ) {
              this.notificationService.viewToast('info', 'Please fill in Guardian details', 'Guardian(s) are mandatory for minors');
              this.addGuardiansArray();
            } else {
              currentRecord.done = true;
            }
          } else {
            if (response.ContactPersons?.length > 0) {
              currentRecord.ContactPersons = currentRecord.ContactPersons.map((contact: any) => {
                const matchingContact = response.ContactPersons.find(
                  (ogContact: any) => contact.Phone === ogContact.Phone
                );
                return matchingContact 
                  ? {...contact, ContactID: Number(matchingContact.ContactID)}
                  : contact;
              });
            }
            currentRecord.done = true;
          }

          if (data.IDNoImage && data.IDNoImage.stringValue.length > 5 && response.IdURL) {
            currentRecord = {...currentRecord, ...{IDURL: response.IdURL, SavedID: currentRecord.IDNoImage}};
            if (relation != 'self') {
              this.estateControlService.removeFromDocSummary('ID', 'Beneficiary', currentRecord.BeneficiaryID);
              this.estateControlService.updateDocSummary(response.IdURL);
            }
          }
          if (data.KraPinImage && data.KraPinImage.stringValue.length > 5 && response.KraURL) {
            currentRecord = {...currentRecord, ...{KraURL: response.KraURL, SavedPIN: currentRecord.KraPinImage}};
            if (relation != 'self') {
              this.estateControlService.removeFromDocSummary('PIN', 'Beneficiary', currentRecord.BeneficiaryID);
              this.estateControlService.updateDocSummary(response.KraURL);
            }
          }
          if (data.BirthCertImage && data.BirthCertImage.stringValue.length > 5 && response.BirthCertURL) {
            currentRecord = {...currentRecord, ...{BirthCertURL: response.BirthCertURL, SavedBirthCert: currentRecord.BirthCertImage}};
            this.estateControlService.removeFromDocSummary('BirthCert', 'Beneficiary', currentRecord.BeneficiaryID);
            this.estateControlService.updateDocSummary(response.BirthCertURL);
          }
          if (data.RegCertImage && data.RegCertImage.stringValue.length > 5 && response.RegCertURL) {
            currentRecord = {...currentRecord, ...{RegCertURL: response.RegCertURL, SavedRegCert: currentRecord.RegCertImage}};
            this.estateControlService.removeFromDocSummary('RegCert', 'Beneficiary', currentRecord.BeneficiaryID);
            this.estateControlService.updateDocSummary(response.RegCertURL);
          }
          
          Object.assign(currentBens[index], currentRecord);
          if (this.planType == 0) {
            this.estateControlService.updateRecord(3, currentBens);
          } else {
            this.estateControlService.updateRecordWill(5, currentBens);
          }

        } else {
          if (record.value.Category.toLowerCase() === 'individual') {
            currentRecord.personalDone = true;
            currentRecord.contactsDone = true;
            Object.assign(currentBens[index], currentRecord);

            if (this.planType == 0) {
              this.estateControlService.updateRecord(3, currentBens);
              if (this.beneficiaries.length <= 1) this.estateControlService.updatePrivateTrustProgress('Beneficiaries');
            } else {
              this.estateControlService.updateRecordWill(5, currentBens);
              if (this.beneficiaries.length <= 1) this.estateControlService.updateWillProgress('Beneficiaries');
            }

            // disable DOB input from getting updated on the current formgroup
            this.getBensFormGroup(index).get('DOB')?.disable();
            
            if (this.getDateDifference(record.value.DOB)) {
              this.validationService.addOrRemoveValidator(true,
                this.getBensFormGroup(index).get('IDNoImage')!);

              this.validationService.addOrRemoveValidator(true,
                this.getBensFormGroup(index).get('KraPinImage')!);

            } else {
              this.validationService.addOrRemoveValidator(
                true,
                this.getBensFormGroup(index).get('BirthCertImage')!);
            }

            setTimeout(() => {
              this.cdr.detectChanges();
            });

          } else {
            if (currentBens[index].personalDone) {
              if (response.ContactPersons?.length > 0) {
                currentRecord.ContactPersons = currentRecord.ContactPersons.map((contact: any) => {
                  const matchingContact = response.ContactPersons.find(
                    (ogContact: any) => contact.Phone === ogContact.Phone
                  );
                  return matchingContact 
                    ? {...contact, ContactID: Number(matchingContact.ContactID)}
                    : contact;
                });
              }
              currentRecord.contactsDone = true;
              Object.assign(currentBens[index], currentRecord);

              if (this.planType == 0) {
                this.estateControlService.updateRecord(3, currentBens);
              } else {
                this.estateControlService.updateRecordWill(5, currentBens);
              }

              this.validationService.addOrRemoveValidator(true,
                this.getBensFormGroup(index).get('RegCertImage')!);

              this.validationService.addOrRemoveValidator(true,
                this.getBensFormGroup(index).get('KraPinImage')!);

              setTimeout(() => {
                this.cdr.detectChanges();
              });

            } else {
              currentRecord.personalDone = true;

              if (record.value.Category === this.BeneficiaryOptions[1]) { // institutions setup for contact persons
                try {
                  this.validationService.addOrRemoveValidator(
                    true,
                    this.getContactsFormGroup(index, 0 ).get('FirstName')!
                  );
                  this.validationService.addOrRemoveValidator(
                    true,
                    this.getContactsFormGroup(index, 0 ).get('LastName')!
                  );
                  
                  this.validationService.addOrRemoveOneValidator(
                    true,
                    this.getContactsFormGroup(index, 0 ).get('Phone')!,
                    Validators.required
                  );
        
                  this.validationService.addOrRemoveSpecificValidators(
                    true,
                    this.getContactsFormGroup(index, 0 ).get('Email')!,
                    [
                      Validators.required,
                      Validators.email]
                  );
                } catch (error) { }
              }
              Object.assign(currentBens[index], currentRecord);

              if (this.planType == 0) {
                this.estateControlService.updateRecord(3, currentBens);
                if (this.beneficiaries.length <= 1) this.estateControlService.updatePrivateTrustProgress('Beneficiaries');
              } else {
                this.estateControlService.updateRecordWill(5, currentBens);
                if (this.beneficiaries.length <= 1) this.estateControlService.updateWillProgress('Beneficiaries');
              }
            }
          }
        }
        if (!environment.production) console.log('>> New Beneficiaries !! \n', this.beneficiaries);
        this.notificationService.viewToast('success', tsMessage);
        this.updateDoneStatus(index, currentRecord);

      } else {
        this.notificationService.viewToast('error', response.Message);
      }
    } catch (error) {
      console.error(':: Error !! ', error);
      this.notificationService.viewToast('error', 'An unexpected error occurred');
    }
    this.notificationService.isLoading(false);
  }
  async saveGuardians(): Promise<void> {
    this.loadingText = 'Saving Guardians';
    this.notificationService.isLoading(true);

    try {
      let guardians = [];
      let allDone = true;

      let currentBens = this.beneficiaries;
      let latestRecord = currentBens[currentBens.length-1];

      // Add final form group to array first:
      this.updateGuardianObject();

      for (let i=0; i<this.guardians.length; i++) {
        let data = this.guardians[i];

        data = {
          ...data,
          ...{
            BeneficiaryID: latestRecord.BeneficiaryID,
            TrustID: this.estateControlService.TrustID,
            AccountType: this.estateControlService.planType == 0 ? 'PrivateTrust' : 'WillCreation'
          }
        };

        if (this.guardians.length === 1) {
          data = {...data, ...{Category: 'Principal Guardian'}};
        }

        if (data.hasOwnProperty('done')) {
          delete data['done'];
        }

        const response = await this.apiService.postRequest(
          environment.baseUrl + Constants.estateGuardianURL, data);  
        // console.log('::::: RESPONSE ::::::\n', response);

        if (response.Status === 1) {
          data = {...data, ...{GuardianID: response.GuardianID, done: true}};

          if (response.IdURL) {
            this.estateControlService.updateDocSummary(response.IdURL);
            data = {...data, ...{IDURL: response.IdURL}};
          }
          if (response.KraURL) {
            this.estateControlService.updateDocSummary(response.KraURL);
            data = {...data, ...{KraURL: response.KraURL}};
          }

          if (data.hasOwnProperty('TrustID')) {
            delete data['TrustID'];
          }
          if (data.hasOwnProperty('AccountType')) {
            delete data['AccountType'];
          }
          if (data.hasOwnProperty('BeneficiaryID')) {
            delete data['BeneficiaryID'];
          }
          
          guardians.push(data);

        } else {
          allDone = false;
        }
      }

      if (allDone) {
        this.guardians = guardians;
        this.autofilled = false;

        latestRecord.done = true;
        const updatedRecord = {...latestRecord, ...{Guardians: this.guardians}};
        Object.assign(currentBens[currentBens.length-1], updatedRecord);

        if (this.planType == 0) {
          this.estateControlService.updateRecord(3, currentBens);
        } else {
          this.estateControlService.updateRecordWill(5, currentBens);
        }
        if (!environment.production) {
          console.log('>> Saved Beneficiary Guardians !! \n', currentBens);
        }
        this.notificationService.viewToast('success', 'Guardians saved');
        this.updateDoneStatus(currentBens.length-1, updatedRecord);

      } else this.notificationService.viewToast('error', 'Not all Guardians were saved', 'Please try again');

    } catch (error) {
      console.error(':: Error !! ', error);
      this.notificationService.viewToast('error', 'An unexpected error occured');
    }
    this.notificationService.isLoading(false);
    this.loadingText = 'Saving Beneficiary';
  }

  async updateProgress(): Promise<void> {
    if (!this.beneficiaries[this.beneficiaries.length-1].done && this.benForm.valid) await this.submitForm();
    this.saveProgress();
  }


  // ==============  EXPANSION SECTION   =================
  @ViewChild('beneficiaryInfo') beneficiaryInfo!: TemplateRef<any>;
  @ViewChild('contactInfo') contactInfo!: TemplateRef<any>;
  @ViewChild('uploadDocs') uploadDocs!: TemplateRef<any>;
  @ViewChild('guardianInfo') guardianInfo!: TemplateRef<any>;


  generatePanelsForBeneficiary = (i: number): ExpansionPanel[] => {
    // Fallback if templates not ready
    if (!this.beneficiaryInfo || !this.contactInfo || !this.uploadDocs || !this.guardianInfo || !this.beneficiaries[i]) {
      return [];
    }
    const isIndividual = this.getBensFormGroup(i).get('Category')?.value === this.BeneficiaryOptions[0];
    
    if (isIndividual) {
      // INDIVIDUALS
      const dobValue = this.getBensFormGroup(i).get('DOB')?.value;

      if (!dobValue || this.getDateDifference(dobValue) === true) { // Adult
        return [
          {
            title: 'Beneficiary Information',
            contentTemplate: this.beneficiaryInfo,
            context: { index: i },
            expanded: !(this.beneficiaries[i].personalDone),
            done: (this.beneficiaries[i].personalDone)
          },
          {
            title: 'Upload Documents',
            contentTemplate: this.uploadDocs,
            context: { index: i },
            disabled: !(this.beneficiaries[i].contactsDone),
            expanded: (this.beneficiaries[i].contactsDone && !this.beneficiaries[i].done),
            done: (this.beneficiaries[i].done)
          }
        ];
      } else { // Minor
        return [
          {
            title: 'Beneficiary Information',
            contentTemplate: this.beneficiaryInfo,
            context: { index: i },
            expanded: !(this.beneficiaries[i].personalDone),
            done: (this.beneficiaries[i].personalDone)
          },
          {
            title: 'Upload Documents',
            contentTemplate: this.uploadDocs,
            context: { index: i },
            disabled: !(this.beneficiaries[i].contactsDone),
            expanded: (this.beneficiaries[i].contactsDone && !this.beneficiaries[i].docsDone),
            done: (this.beneficiaries[i].docsDone)
          },
          {
            title: 'Guardian Information',
            contentTemplate: this.guardianInfo,
            context: { index: i },
            disabled: !(this.beneficiaries[i].docsDone),
            expanded: (this.beneficiaries[i].docsDone && !this.beneficiaries[i].done),
            done: (this.beneficiaries[i].done)
          }
        ];
      }
    } else {
      // INSTITUTIONS
      return [
        {
          title: 'Beneficiary Information',
          contentTemplate: this.beneficiaryInfo,
          context: { index: i },
          expanded: !(this.beneficiaries[i].personalDone),
          done: (this.beneficiaries[i].personalDone)
        },
        {
          title: 'Contact Person Information',
          contentTemplate: this.contactInfo,
          context: { index: i },
          disabled: !(this.beneficiaries[i].personalDone),
          expanded: (this.beneficiaries[i].personalDone && !this.beneficiaries[i].contactsDone),
          done: (this.beneficiaries[i].contactsDone)
        },
        {
          title: 'Upload Documents',
          contentTemplate: this.uploadDocs,
          context: { index: i },
          disabled: !(this.beneficiaries[i].contactsDone),
          expanded: (this.beneficiaries[i].contactsDone && !this.beneficiaries[i].done),
          done: (this.beneficiaries[i].done)
        }
      ];
    }
  }

  updateDoneStatus(i: number, value: any) {
    // Ensure panels array exists
    this.benPanels = this.benPanels || [];
    
    // Initialize if missing or invalid
    if (!this.benPanels[i]) {
      this.benPanels[i] = this.generatePanelsForBeneficiary(i);
    }

    const panels = this.benPanels[i];
    const isIndividual = this.getBensFormGroup(i).get('Category')?.value === this.BeneficiaryOptions[0];
    const dobValue = this.getBensFormGroup(i).get('DOB')?.value;
    
    // Update panel states while preserving context
    if (panels[0]) {
      panels[0].done = value.personalDone;
      // Preserve existing context or initialize if missing
      panels[0].context = panels[0].context || { index: i };
      panels[0].expanded = !value.personalDone;
    }
    
    if (panels[1]) {
      panels[1].done = isIndividual ? value.docsDone : value.contactsDone;
      panels[1].context = panels[1].context || { index: i };
      panels[1].disabled = isIndividual ? !value.contactsDone : !value.personalDone;
      panels[1].expanded = isIndividual ? !value.docsDone : !value.contactsDone;
    }
    
    if (panels[2]) {
      panels[2].done = value.done;
      panels[2].context = panels[2].context || { index: i };
      panels[2].disabled = isIndividual ? !value.docsDone : !value.contactsDone;
      panels[2].expanded = !value.done;
    }

    setTimeout(()=> {
      try {
        if (value.BeneficiaryID && !value.done && i == this.beneficiaries.length-1) {
          const benName = isIndividual ? `${value.FirstName} ${value.LastName}` : `${value.InstitutionName}`
          const isAdult = this.getDateDifference(dobValue);

          this.notificationService.viewToast(
            'info',
            `Beneficiary ${benName}`,
            `Please continue to ${
              isIndividual && isAdult && value.docsDone || !isIndividual && value.contactsDone ? panels[2].title :
              panels[1].title 
            }`
          );
        }
        
      } catch (error) {}
    })
  }

  async updateRecord(index: number) {
    await this.saveBeneficiary(index, this.getBensFormGroup(index));
  }

  private setupFormChangeListeners() {
    this.cleanupFormChangeListeners();

    // Use debounce to batch multiple changes
    const debounceTimeMs = 300;
    
    this.getBens().controls.forEach((control, index) => {
      const formGroup = control as FormGroup;
    
      // Listen to DOB changes
      const dobSub = formGroup.get('DOB')?.valueChanges.pipe(
        debounceTime(debounceTimeMs),
        distinctUntilChanged()
      ).subscribe(() => {
        this.ngZone.run(() => {
          this.benPanels[index] = this.generatePanelsForBeneficiary(index);
          this.cdr.markForCheck();
        });
      });

      if (dobSub) this.formChangesSubscription.push(dobSub);

      // General form changes - no need to trigger panel updates
      const formSub = formGroup.valueChanges.subscribe();
      this.formChangesSubscription.push(formSub);
    });
  }
  private setupFormChangeListenersForIndex(index: number) {
    const control = this.getBens().at(index) as FormGroup;

    // Use debounce to batch multiple changes
    const debounceTimeMs = 300;
    
    // Clean up existing subscription if any
    const existingSub = this.formChangesSubscription[index];
    if (existingSub) {
      existingSub.unsubscribe();
    }

    // Listen to DOB changes
    const dobSub = control.get('DOB')?.valueChanges.pipe(
      debounceTime(debounceTimeMs),
      distinctUntilChanged()
    ).subscribe(() => {
      this.ngZone.run(() => {
        this.benPanels[index] = this.generatePanelsForBeneficiary(index);
        this.cdr.markForCheck();
      });
    });

    if (dobSub) this.formChangesSubscription[index] = dobSub;
  }
  private cleanupFormChangeListeners() {
    this.formChangesSubscription.forEach(sub => sub.unsubscribe());
    this.formChangesSubscription = [];
  }

  private loggedErrors = new Set<string>();

  confirmFormMatchesRecord(index: number): boolean {
    try {
      const ben = this.beneficiaries[index];
      const formGroup = this.getBensFormGroup(index);
      
      if (!ben?.done || !formGroup) {
        return true;
      }

      const isIndividual = ben.Category === this.BeneficiaryOptions[0];

      // Compare all relevant fields
      if (isIndividual) {
        const dobValue = this.getBensFormGroup(index).get('DOB')?.value;
        const isAdult = this.getDateDifference(dobValue);

        // Fields to compare
        const fieldsToCompare = [
          'Title', 'FirstName', 'MiddleName', 'LastName',
          'Phone', 'Email', 'KraPin', 'Gender', 'Relationship', 'Other'
        ];

        const basicFieldsMatch = fieldsToCompare.every(field => {
          const recordValue = ben[field];
          const formValue = formGroup.get(field)?.value;
          const valuesMatch = (formValue || '') === (recordValue || '');
          
          // If either value is empty, consider this field as matching
          return valuesMatch;
        });

        let filesMatch = true;

        if (isAdult) {
          if (ben?.KraPinImage?.name) {
            filesMatch =
              ben.IDNoImage.name === ben.SavedID.name &&
              ben.KraPinImage.name === ben.SavedPIN.name;
          } else {
            filesMatch = ben.IDNoImage.name === ben.SavedID.name;
          }
          
        } else {
          filesMatch =
            ben.BirthCertImage.name === ben.SavedBirthCert.name;
        }

        return basicFieldsMatch && filesMatch;

      } else {
        // Fields to compare
        const fieldsToCompare = [
          'InstitutionName', 'BusinessNature', 'OrgRegNum', 'OrgAddress', 
          'KraPin'
        ];

        const basicFieldsMatch = fieldsToCompare.every(field => {
          const recordValue = ben[field];
          const formValue = formGroup.get(field)?.value;
          const valuesMatch = (formValue || '') === (recordValue || '');
          
          // If either value is empty, consider this field as matching
          return valuesMatch;
        });

        let filesMatch = true;

        filesMatch =
          ben.RegCertImage.name === ben.SavedRegCert.name &&
          ben.KraPinImage.name === ben.SavedPIN.name;

        return basicFieldsMatch && filesMatch;
      }
    } catch (error: any) {
      const errorKey = `error-${index}-${error.message}`;
      if (!this.loggedErrors.has(errorKey)) {
        console.error('Error in confirmFormMatchesRecord:', error);
        this.loggedErrors.add(errorKey);
      }
      return false;
    }
  }

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++

  async submitForm(): Promise<void> {
    if (this.beneficiaries[this.beneficiaries.length-1].done === true) {
      this.navigateFunction();
      
    } else if (
      this.beneficiaries[this.beneficiaries.length-1].docsDone &&
      this.getBensFormGroup(this.getBens().length-1).get('DOB')?.value !== '' &&
      this.getDateDifference(this.getBensFormGroup(this.getBens().length-1).get('DOB')?.value) === false
    ){
      this.saveGuardians();
    } else {
      const bens = this.benForm.get('beneficiaries') as FormArray;
      this.saveBeneficiary(bens.length - 1, this.getBensFormGroup(bens.length - 1));
    }
  }

  async navigateFunction(): Promise<void> {
    if (this.planType == 0) {
      await this.estateControlService.changeTab(4);
      this.estateControlService.page3Done = true;
    } else {
      await this.estateControlService.changeTabWill(6);
      this.estateControlService.page5Done = true;
    }
  }
}
